import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'

import { useToast } from '@wartek-id/toast-v2'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'

import { assetUrl } from 'utils/url'
import {
  useAPIKinerja,
  useAPIUpdateState,
  apiPaths,
} from 'api/kinerja/useAPIKinerja'
import { useGetKinerjaQuerykeys } from 'api/kinerja/utils/api'
import { resolvePerencanaanDetailData } from 'app/KinerjaGuruPage/utils/perencanaan'
import { Detail } from 'app/KinerjaGuruPage/components/perencanaan/Detail'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import useSKPTrackEvent from 'app/KinerjaPage/hooks/useSKPTrackEvent'

import type { PerencanaanGuruDetailData } from 'app/KinerjaGuruPage/types/perencanaan'

type RangkumanProps = {
  onBack: () => void
}

export function Rangkuman({ onBack }: RangkumanProps) {
  const router = useRouter()
  const toast = useToast()
  const queryClient = useQueryClient()
  const perencanaanQueryKey = useGetKinerjaQuerykeys(apiPaths.kinerja())
  const { trackEvent } = useSKPTrackEvent()

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
      {
        tipe: 'SPF',
      }
    )

  const resolvedPerencanaanData: PerencanaanGuruDetailData = useMemo(
    () => resolvePerencanaanDetailData(perencanaanData?.data),
    [perencanaanData?.data]
  )

  // -----------------------------------------
  // Scroll to top on mount to prevent scroll jump
  // -----------------------------------------
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  // -----------------------------------------
  // Handle submission
  // -----------------------------------------
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false)

  const handleCloseSubmitDialog = () => {
    setIsSubmitDialogOpen(false)
  }

  const handleOpenSubmitDialog = () => {
    // Send track event
    trackEvent({
      event_name: 'CLICK_KINERJA_PLANNING_TO_POP_UP_CONFIRMATION',
      screen_name: 'KINERJA_PLANNING_PERILAKU_KERJA_PAGE',
      kinerja_planning_id: perencanaanData?.data?.id,
      kinerja_planning_status: perencanaanData?.data?.status,
    })

    // Open the submit dialog
    setIsSubmitDialogOpen(true)
  }

  const updateStateMutation = useAPIUpdateState()

  const handleSubmit = () => {
    // Send track event
    trackEvent({
      event_name: 'CLICK_KINERJA_PROPOSE_PLANNING',
      screen_name: 'KINERJA_PLANNING_PENGAJUAN_POP_UP',
      kinerja_planning_id: perencanaanData?.data?.id,
      kinerja_planning_status: perencanaanData?.data?.status,
    })

    // Do the update state mutation
    updateStateMutation.mutate(
      {
        skpId: perencanaanData?.data?.id,
        tipe: 'GPK',
      },
      {
        onSuccess: () => {
          handleCloseSubmitDialog()
          toast({
            message: 'Berhasil mengajukan Perencanaan',
            type: 'success',
          })
          router.push(PENGELOLAAN_KINERJA_GURU_PATH)
          queryClient.invalidateQueries(perencanaanQueryKey)
        },
        onError: () => {
          handleCloseSubmitDialog()
          toast({
            message: 'Gagal mengajukan Perencanaan',
          })
        },
      }
    )
  }

  const isSubmitting = updateStateMutation.isLoading

  return (
    <>
      <div className="p-9">
        <div className="space-y-1 mb-9">
          <Text variant="heading-md">Rangkuman Perencanaan Kinerja Anda</Text>
          <ul className="list-disc list-outside pl-5">
            <Text as="li">
              Anda dapat cek ulang dan ubah sebelum mengirim ke Kepala Sekolah.
            </Text>
            <Text as="li">
              Format kalimat Perencanaan Kinerja sudah kami sesuaikan dengan
              aturan yang berlaku. Mohon dicek, ya.
            </Text>
          </ul>
        </div>

        <Detail
          isLoading={isPerencanaanLoading}
          data={resolvedPerencanaanData}
        />
      </div>

      <div className="border-t border-subdued px-6 py-4 flex justify-end gap-5">
        <Button
          size="lg"
          color="white"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Ubah
        </Button>
        <Button
          color="blue"
          size="lg"
          isLoading={isSubmitting}
          disabled={isPerencanaanLoading || isSubmitting}
          onClick={handleOpenSubmitDialog}
        >
          Ajukan Perencanaan
        </Button>
      </div>

      <Dialog
        isOpen={isSubmitDialogOpen}
        closeOnOverlayClick={false}
        size="sm"
        onClose={handleCloseSubmitDialog}
      >
        <DialogOverlay />
        <DialogContent
          aria-labelledby="confirmation-dialog-title"
          aria-describedby="confirmation-dialog-description"
        >
          <div className="p-6 space-y-4">
            <img
              src={assetUrl('/illustrations/img-publish-data.png')}
              alt=""
              width={84}
              height={84}
            />

            <Text variant="display-sm" as="h2" id="confirmation-dialog-title">
              Konfirmasi untuk Ajukan Perencanaan
            </Text>

            <Text variant="body-lg" id="confirmation-dialog-description">
              Perencanaan tidak dapat diubah lagi dan akan diperiksa oleh Kepala
              Sekolah.
            </Text>

            <div className="flex gap-5">
              <Button
                color="white"
                size="lg"
                className="w-full"
                disabled={isSubmitting}
                onClick={handleCloseSubmitDialog}
              >
                Kembali
              </Button>
              <Button
                color="blue"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                size="lg"
                className="w-full"
                type="button"
                onClick={handleSubmit}
              >
                Konfirmasi
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
